import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Box,
  IconButton,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { useEffect, useMemo, useState } from "react";
import { COLORS } from "../../../assets/theme";
import { IField } from "../../../types/table/interfaces.type";
import CustomCheckbox from "../../CustomCheckbox";
import React from "react";

interface IDataTableBodyProps<T> {
  fields: IField<T>[];
  content: T[];
  actions?: {
    label: string;
    icon: React.ReactNode | ((row: any) => React.ReactNode);
    handler: (row: T) => void;
  }[];
  renderActions?: (row: T) => React.ReactElement[];
  shadeRow?: (row: T) => boolean;
  shadeBg?: string | ((row: T) => string | undefined);
  shadeColor?: string;
  checkboxSelect?: boolean;
  onRowSelect?: (row: T, checked: boolean) => void;
  isRowSelected?: (row: T) => boolean;
  highlights?: Record<string, boolean>[];
  onRowClick?: (row: T) => void;
}

function DataTableBody<T>({
  fields,
  content = [],
  highlights = [],
  actions = [],
  renderActions,
  shadeRow,
  shadeBg,
  shadeColor,
  checkboxSelect = false,
  onRowSelect,
  isRowSelected,
  onRowClick,
}: IDataTableBodyProps<T>) {
  const theme = useTheme();
  const [highlightedRowIndex, setHighlightedRowIndex] = useState<number | null>(
    null,
  );

  useEffect(() => {
    setHighlightedRowIndex(null);
  }, [content]);

  const handleRowPress = (e: React.MouseEvent, rowIndex: number) => {
    // Only toggle when pressing a non-interactive area of the row
    const target = e.target as HTMLElement;
    if (
      target.closest(
        "button, a, input, textarea, select, [role='button'], [role='checkbox']",
      )
    )
      return;
    setHighlightedRowIndex((prev) => (prev === rowIndex ? null : rowIndex));
  };

  const getRowSx = (row: T) => {
    if (shadeRow && shadeRow(row)) {
      let backgroundColor: string | undefined = "#FFEEAA";
      if (shadeBg) {
        backgroundColor =
          typeof shadeBg === "function" ? shadeBg(row) : shadeBg;
      }

      const textColor =
        shadeColor ??
        (theme.palette.mode === "light"
          ? theme.palette.grey[600]
          : theme.palette.grey[500]);

      return {
        backgroundColor,
        color: textColor,
        transition: theme.transitions.create(
          ["background-color", "background-image", "color"],
          { duration: theme.transitions.duration.shortest },
        ),
        borderRadius: "8px",
        "& td": {
          color: textColor,
          alignText: "center",
        },
      };
    }
    return {};
  };

  return (
    <TableBody
      sx={{
        "& .MuiTableCell-root": {
          padding: "4px 8px",
          height: "20px",
          width: "max-content",
          lineHeight: "20px",
        },
        "& .MuiTableRow-root": {
          height: "20px",
        },
      }}
    >
      {content.length > 0 ? (
        content.map((row, rowIndex) => (
          <TableRow
            key={rowIndex}
            onClick={(e) => {
              handleRowPress(e, rowIndex);
              onRowClick?.(row);
            }}
            sx={{
              ...getRowSx(row),
              ...(shadeRow && shadeRow(row)
                ? {}
                : {
                    backgroundColor:
                      rowIndex % 2 === 0
                        ? theme.palette.grey[100]
                        : theme.palette.common.white,
                  }),
              ...(onRowClick
                ? {
                    cursor: "pointer",
                    "&:hover": {
                      filter: "brightness(0.98)",
                    },
                  }
                : {}),
              ...(highlightedRowIndex === rowIndex && {
                backgroundColor: "rgba(163, 148, 85, 0.22)",
                boxShadow: "inset 3px 0 0 0 #A39455",
              }),
            }}
          >
            {checkboxSelect && (
              <TableCell
                align="center"
                sx={{
                  borderBottom: "none",
                  textAlign: "center",
                  marginLeft: "center",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <Box sx={{ ml: 1 }}>
                  <CustomCheckbox
                    checked={isRowSelected ? isRowSelected(row) : false}
                    onChange={(e) => {
                      e.stopPropagation();
                      onRowSelect && onRowSelect(row, e.target.checked);
                    }}
                    ariaLabel="Select row"
                  />
                </Box>
              </TableCell>
            )}
            {fields.map((field, fieldIndex) => (
              <AsyncTableCell<T>
                row={row}
                field={field}
                key={fieldIndex}
                highlight={highlights?.[rowIndex]?.[field.key] ?? false}
              />
            ))}
            {(actions.length > 0 || renderActions) && (
              <TableCell
                sx={{ borderBottom: "none", textAlign: "center" }}
                onClick={(e) => e.stopPropagation()}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {renderActions
                    ? renderActions(row)
                    : actions.map((action, actionIndex) => (
                        <IconButton
                          key={actionIndex}
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            action.handler(row);
                          }}
                          aria-label={action.label}
                        >
                          {typeof action.icon === "function"
                            ? action.icon(row)
                            : action.icon}
                        </IconButton>
                      ))}
                </Box>
              </TableCell>
            )}
          </TableRow>
        ))
      ) : (
        <TableRow>
          <TableCell
            colSpan={
              fields.length +
              (actions.length > 0 || renderActions ? 1 : 0) +
              (checkboxSelect ? 1 : 0)
            }
            sx={{ textAlign: "center", borderBottom: "none", py: 2 }}
          >
            No data available
          </TableCell>
        </TableRow>
      )}
    </TableBody>
  );
}

interface AsyncTableCellProps<T> {
  key: number;
  row: T;
  field: IField<T>;
  highlight?: boolean;
}

function AsyncTableCell<T>({
  row,
  field,
  key,
  highlight = false,
}: AsyncTableCellProps<T>) {
  // Memoize so a new Promise isn't created on every parent re-render
  const promise = useMemo(
    () => (field.transform ? field.transform(row) : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [row, field],
  );

  if (field.options) {
    return (
      <QueriedTableCell
        key={key}
        options={field.options(row)}
        highlight={highlight}
      />
    );
  }
  if (field.transform) {
    return (
      <PromisedTableCell promise={promise} key={key} highlight={highlight} />
    );
  }
  return (
    <TableCell
      key={key}
      sx={{
        borderBottom: "none",
        textAlign: "center",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: highlight ? "#FFF59D" : undefined,
        width: "max-content",
      }}
    >
      <SyncTableCellValue value={row[field.key]} />
    </TableCell>
  );
}
interface PromisedTableCellProps {
  key: number;
  promise: Promise<any> | undefined;
  highlight?: boolean;
}

function PromisedTableCell({
  key,
  promise,
  highlight,
}: PromisedTableCellProps) {
  const [element, setElement] = useState<JSX.Element>();
  useEffect(() => {
    (async () => {
      try {
        const newValue = await promise;
        setElement(<SyncTableCellValue value={newValue} />);
      } catch (error: any) {
        setElement(<ErrorComponent />);
      }
    })();
  }, [promise]);

  return (
    <TableCell
      key={key}
      sx={{
        borderBottom: "none",
        textAlign: "center",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: highlight ? "#FFF59D" : undefined,
        width: "max-content",
      }}
    >
      {element}
    </TableCell>
  );
}

function ErrorComponent() {
  return (
    <TableCell
      sx={{ borderBottom: "none", textAlign: "center", color: COLORS.RED }}
    >
      <WarningAmberIcon sx={{ color: COLORS.RED, width: 23, height: 23 }} />
    </TableCell>
  );
}

interface QueriedTableCellProps {
  options: UseQueryOptions<any, any>;
  key: number;
  highlight?: boolean;
}

function QueriedTableCell({ options, highlight }: QueriedTableCellProps) {
  const query = useQuery(options);

  const getDisplayValue = (data: unknown): JSX.Element => {
    let value = "";
    if (data == null) {
      value = "-";
    } else if (typeof data === "string" || typeof data === "number") {
      value = String(data);
    } else if (typeof data === "object") {
      const keys = Object.keys(data);
      if (keys.length === 1) {
        value = String((data as Record<string, any>)[keys[0]]);
      } else {
        value = JSON.stringify(data, null, 2);
      }
    } else {
      value = "-";
    }
    return <div style={{ textAlign: "center" }}>{value}</div>;
  };

  if (query.isError) {
    return (
      <TableCell sx={{ borderBottom: "none", textAlign: "center" }}>
        -
      </TableCell>
    );
  }

  return (
    <TableCell
      sx={{
        borderBottom: "none",
        textAlign: "center",
        justifyContent: "center",
        alignContent: "center",
        backgroundColor: highlight ? "#FFF59D" : undefined,
        width: "max-content",
      }}
    >
      <SyncTableCellValue value={getDisplayValue(query.data)} />
    </TableCell>
  );
}

interface SyncTableCellValueProps {
  value: any;
}

// Fix regex: remove unnecessary escape character for - inside character class
const iso8601DateTimeRegex =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?$/;

function isValidDate(dateString: string): boolean {
  return iso8601DateTimeRegex.test(dateString);
}

function formatDateString(dateString: string): string {
  if (!isValidDate(dateString)) {
    return dateString;
  }
  const parsedDate = parseISO(dateString);

  return formatInTimeZone(parsedDate, "UTC", "dd-MM-yyyy HH:mm:ss a");
}

function safeStringify(val: any): string {
  try {
    return JSON.stringify(val, null, 2);
  } catch (err) {
    return String(val);
  }
}

function SyncTableCellValue({ value }: SyncTableCellValueProps) {
  if (React.isValidElement(value)) {
    return value;
  }

  const formatValue = (val: any): string => {
    if (val === undefined || val === null) {
      return "-";
    }
    if (val instanceof Date) {
      return formatInTimeZone(val, "UTC", "dd-MM-yyyy HH:mm:ss");
    }
    if (typeof val === "string" && isValidDate(val)) {
      return formatDateString(val);
    }
    if (typeof val === "object") {
      return safeStringify(val);
    }
    return String(val);
  };

  const formattedValue = formatValue(value);

  return value !== undefined && value !== null ? (
    typeof value === "object" ? (
      <Typography
        component="pre"
        sx={{
          margin: 0,
          fontSize: "0.90rem",
          textAlign: "center",
          width: "100%",
          display: "flex",
          justifyContent: "center",
        }}
      >
        {formattedValue}
      </Typography>
    ) : (
      <Typography
        sx={{
          margin: 0,
          fontSize: "0.90rem",
          textAlign: "center",
          width: "100%",
          display: "flex",
          justifyContent: "center",
        }}
      >
        {formattedValue}
      </Typography>
    )
  ) : (
    <Typography
      sx={{
        textAlign: "center",
        width: "100%",
        display: "flex",
        justifyContent: "center",
      }}
    >
      -
    </Typography>
  );
}

export default DataTableBody;
